import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

const ACCEPTED_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
const MIN_DURATION = 5;
const MAX_DURATION = 15;

interface Upload {
  id: string;
  title: string;
  status: 'pending_review' | 'approved' | 'rejected';
  created_at: string;
  thumbnail_url?: string;
  duration_seconds: number;
}

interface UploadsResponse {
  uploads: Upload[];
  total: number;
}

export default function UploadPage() {
  const { user, authToken } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const [uploads, setUploads] = useState<Upload[]>([]);
  const [isLoadingUploads, setIsLoadingUploads] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/');
    }
  }, [user, navigate]);

  const fetchUploads = useCallback(() => {
    if (!authToken) return;

    setIsLoadingUploads(true);
    fetch(`${API_BASE}/uploads`, {
      headers: {
        Authorization: `Bearer ${authToken}`,
      },
    })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load uploads');
        return res.json();
      })
      .then((data: UploadsResponse) => {
        setUploads(data.uploads);
      })
      .catch(() => {
        // Silently fail for uploads list
      })
      .finally(() => {
        setIsLoadingUploads(false);
      });
  }, [authToken]);

  useEffect(() => {
    fetchUploads();
  }, [fetchUploads]);

  const validateFile = (selectedFile: File): string | null => {
    if (!ACCEPTED_TYPES.includes(selectedFile.type)) {
      return 'Invalid file type. Please upload MP4, WebM, or MOV files.';
    }
    if (selectedFile.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size is ${MAX_FILE_SIZE / 1024 / 1024}MB.`;
    }
    return null;
  };

  const checkVideoDuration = (selectedFile: File): Promise<number> => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.onloadedmetadata = () => {
        URL.revokeObjectURL(video.src);
        resolve(video.duration);
      };
      video.onerror = () => {
        URL.revokeObjectURL(video.src);
        reject(new Error('Could not read video metadata'));
      };
      video.src = URL.createObjectURL(selectedFile);
    });
  };

  const handleFileSelect = async (selectedFile: File) => {
    setValidationError(null);
    setUploadError(null);
    setUploadSuccess(false);

    const fileError = validateFile(selectedFile);
    if (fileError) {
      setValidationError(fileError);
      return;
    }

    try {
      const duration = await checkVideoDuration(selectedFile);
      if (duration < MIN_DURATION) {
        setValidationError(
          `Video is too short (${duration.toFixed(1)}s). Minimum duration is ${MIN_DURATION} seconds.`
        );
        return;
      }
      if (duration > MAX_DURATION) {
        setValidationError(
          `Video is too long (${duration.toFixed(1)}s). Maximum duration is ${MAX_DURATION} seconds.`
        );
        return;
      }
    } catch {
      setValidationError('Could not read video duration. Please try a different file.');
      return;
    }

    setFile(selectedFile);
    if (!title) {
      setTitle(selectedFile.name.replace(/\.[^/.]+$/, ''));
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      handleFileSelect(droppedFile);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      handleFileSelect(selectedFile);
    }
  };

  const handleUpload = async () => {
    if (!file || !title.trim() || !authToken) return;

    setUploading(true);
    setUploadError(null);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('video', file);
    formData.append('title', title.trim());

    try {
      const xhr = new XMLHttpRequest();

      await new Promise<void>((resolve, reject) => {
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round((e.loaded / e.total) * 100));
          }
        };

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            try {
              const errorData = JSON.parse(xhr.responseText);
              reject(new Error(errorData.error || 'Upload failed'));
            } catch {
              reject(new Error('Upload failed'));
            }
          }
        };

        xhr.onerror = () => reject(new Error('Network error during upload'));

        xhr.open('POST', `${API_BASE}/uploads`);
        xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
        xhr.send(formData);
      });

      setUploadSuccess(true);
      setFile(null);
      setTitle('');
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      fetchUploads();
    } catch (err) {
      setUploadError(
        err instanceof Error ? err.message : 'Upload failed. Please try again.'
      );
    } finally {
      setUploading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending_review':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-500/20 text-yellow-400">
            Pending Review
          </span>
        );
      case 'approved':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (!user) return null;

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold mb-2">Upload a Video Clip</h1>
          <p className="text-arena-muted">
            Submit a short video clip for scene detection battles
          </p>
        </div>

        {/* Upload Form */}
        <div className="card p-6 mb-8">
          {/* Success Message */}
          {uploadSuccess && (
            <div className="bg-green-500/10 border border-green-500/30 text-green-400 px-4 py-3 rounded-lg mb-6 text-sm">
              Upload successful! Your clip will be reviewed before appearing in
              battles.
            </div>
          )}

          {/* Drag and Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
              dragOver
                ? 'border-arena-accent bg-arena-accent/10'
                : file
                  ? 'border-green-500/50 bg-green-500/5'
                  : 'border-arena-border hover:border-arena-muted'
            }`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/webm,video/quicktime"
              onChange={handleInputChange}
              className="hidden"
            />

            {file ? (
              <div>
                <div className="text-3xl mb-2">&#127916;</div>
                <p className="font-medium">{file.name}</p>
                <p className="text-sm text-arena-muted mt-1">
                  {(file.size / 1024 / 1024).toFixed(1)} MB
                </p>
                <p className="text-sm text-arena-accent mt-2">
                  Click or drag to replace
                </p>
              </div>
            ) : (
              <div>
                <div className="text-4xl mb-3 text-arena-muted">&#128228;</div>
                <p className="font-medium mb-1">
                  Drag and drop a video file here
                </p>
                <p className="text-sm text-arena-muted">
                  or click to browse files
                </p>
                <p className="text-xs text-arena-muted mt-3">
                  MP4, WebM, or MOV &middot; Max 50MB &middot; 5-15 seconds
                </p>
              </div>
            )}
          </div>

          {/* Validation Error */}
          {validationError && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mt-4 text-sm">
              {validationError}
            </div>
          )}

          {/* Title Input */}
          <div className="mt-6">
            <label
              htmlFor="clip-title"
              className="block text-sm font-medium mb-2"
            >
              Clip Title
            </label>
            <input
              id="clip-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Give your clip a descriptive title"
              className="w-full px-4 py-3 bg-arena-surface border border-arena-border rounded-xl text-arena-text placeholder-arena-muted focus:outline-none focus:border-arena-accent transition-colors"
            />
          </div>

          {/* Upload Progress */}
          {uploading && (
            <div className="mt-6">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-arena-muted">Uploading...</span>
                <span className="text-arena-accent font-mono">
                  {uploadProgress}%
                </span>
              </div>
              <div className="w-full h-2 bg-arena-border rounded-full overflow-hidden">
                <div
                  className="h-full bg-arena-accent transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Upload Error */}
          {uploadError && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mt-4 text-sm">
              {uploadError}
            </div>
          )}

          {/* Upload Button */}
          <button
            onClick={handleUpload}
            disabled={!file || !title.trim() || uploading}
            className="btn btn-primary w-full mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Uploading...
              </span>
            ) : (
              'Upload Clip'
            )}
          </button>
        </div>

        {/* User's Uploads */}
        <div className="card overflow-hidden">
          <div className="p-4 border-b border-arena-border">
            <h2 className="font-semibold">Your Uploads</h2>
          </div>

          {isLoadingUploads ? (
            <div className="p-8 text-center text-arena-muted">
              <div className="w-6 h-6 border-4 border-arena-accent border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
              Loading uploads...
            </div>
          ) : uploads.length === 0 ? (
            <div className="p-8 text-center text-arena-muted">
              No uploads yet. Submit your first clip above!
            </div>
          ) : (
            <div className="divide-y divide-arena-border">
              {uploads.map((upload) => (
                <div
                  key={upload.id}
                  className="flex items-center justify-between p-4 hover:bg-arena-surface/50"
                >
                  <div className="flex items-center gap-4 min-w-0">
                    {upload.thumbnail_url ? (
                      <img
                        src={upload.thumbnail_url}
                        alt={upload.title}
                        className="w-16 h-10 rounded object-cover flex-shrink-0"
                      />
                    ) : (
                      <div className="w-16 h-10 rounded bg-arena-surface flex items-center justify-center text-arena-muted text-xs flex-shrink-0">
                        No thumb
                      </div>
                    )}
                    <div className="min-w-0">
                      <p className="font-medium text-sm truncate">
                        {upload.title}
                      </p>
                      <p className="text-xs text-arena-muted">
                        {upload.duration_seconds.toFixed(1)}s &middot;{' '}
                        {formatDate(upload.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    {getStatusBadge(upload.status)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
